import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Header from '../components/Header/Header';
import { useParams, useNavigate } from 'react-router-dom';
// import LoadingDots from '../components/LoadingDots/LoadingDots';
import ImageGallery from '../components/ImageGallery/ImageGallery';
import { useAuth } from '../auth/AuthContext';
import socketService from '../services/socketService';

const MAX_SELECTED_IMAGES = 10;

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff'];

const isTopLevelEventImage = (item) => {
  if (item.isFolder) return false;
  const extension = item.name.toLowerCase().substring(item.name.lastIndexOf('.'));
  return IMAGE_EXTENSIONS.includes(extension);
};

/** Same ordering as cjp-client ImageGallery: newest first (S3 lastModified, then key). */
const sortEventImagesNewestFirst = (items) =>
  [...items].sort((a, b) => {
    const ta = a.lastModified != null ? new Date(a.lastModified).getTime() : 0;
    const tb = b.lastModified != null ? new Date(b.lastModified).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return String(b.key || '').localeCompare(String(a.key || ''));
  });

const EventView = () => {
  const { customer, event } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [eventContents, setEventContents] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailGreeting, setEmailGreeting] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Customer/Event selection state
  const [customers, setCustomers] = useState([]);
  const [events, setEvents] = useState([]);
  
  // Socket service ref for cleanup
  const socketCleanupRef = useRef(null);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Check if we have both customer and event parameters
  const hasValidParams = Boolean(customer && event);
  
  // Event ID state for email functionality
  const [eventId, setEventId] = useState(null);
  
  // Branded content state
  const [brandedContentSettings, setBrandedContentSettings] = useState(null);
  const [brandedContentImage, setBrandedContentImage] = useState(null);
  
  // Create a stable reference to setEventContents
  const setEventContentsRef = useRef(setEventContents);
  setEventContentsRef.current = setEventContents;

  const fetchEventId = useCallback(async () => {
    if (!customer || !event) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/events/lookup/${encodeURIComponent(customer)}/${encodeURIComponent(event)}`);
      if (response.data.success && response.data.event) {
        setEventId(response.data.event.id);
      }
    } catch (error) {
      console.error('Error fetching event ID:', error);
      // Don't show error to user as this is just for email functionality
    }
  }, [customer, event]);

  const fetchBrandedContentSettings = useCallback(async () => {
    if (!eventId) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/events/${eventId}/branded-content`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setBrandedContentSettings(response.data.settings);
        setBrandedContentImage(response.data.imageUrl);
      }
    } catch (error) {
      console.error('Error fetching branded content settings:', error);
      // Don't show error to user as this is just for display purposes
    }
  }, [eventId]);

  const fetchEventContents = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/events/${encodeURIComponent(customer)}/${encodeURIComponent(event)}/contents`);
      
      if (!response.data || response.data.length === 0) {
        setEventContents([]);
      } else {
        const filteredContents = response.data.filter(isTopLevelEventImage);
        setEventContents(sortEventImagesNewestFirst(filteredContents));
      }
    } catch (error) {
      console.error('Error fetching event contents:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      setSnackbar({
        open: true,
        message: 'Error fetching event contents',
        severity: 'error',
      });
    }
  }, [customer, event]);

  // Use refs to avoid recreating the handler function
  const userRef = useRef(user);
  const customerRef = useRef(customer);
  const eventRef = useRef(event);
  
  // Update refs when values change
  useEffect(() => {
    userRef.current = user;
    customerRef.current = customer;
    eventRef.current = event;
  }, [user, customer, event]);

  // Handle S3 events from socket service - stable function that doesn't recreate
  const handleS3Event = useCallback((payload) => {
    if (!userRef.current?.id || !customerRef.current || !eventRef.current) {
      return;
    }
    
    const keyParts = payload.key.split('/');
    if (keyParts.length !== 4) {
      return;
    }

    const [eventUserId, eventCustomerName, eventName, imageName] = keyParts;

    if (
      String(eventUserId) !== String(userRef.current.id) ||
      eventCustomerName !== customerRef.current ||
      eventName !== eventRef.current
    ) {
      console.log('[EventView] S3 event does not match current event:', {
        eventUserId, eventCustomerName, eventName,
        currentUser: userRef.current.id, currentCustomer: customerRef.current, currentEvent: eventRef.current
      });
      return;
    }

    const extension = imageName.toLowerCase().substring(imageName.lastIndexOf('.'));
    if (!IMAGE_EXTENSIONS.includes(extension)) {
      return;
    }

    const s3Key = payload.key || `${eventUserId}/${eventCustomerName}/${eventName}/${imageName}`;
    const lastModified =
      payload.lastModified != null
        ? payload.lastModified
        : payload.time != null
          ? payload.time
          : new Date().toISOString();
    const newImage = {
      key: s3Key,
      name: imageName,
      lastModified,
      url: payload.url,
      isFolder: false,
      size: payload.size || 0
    };

    setEventContentsRef.current((prevContents) => {
      const fileExists = prevContents.some((item) => {
        const itemKey = typeof item.key === 'string' ? item.key : '';
        const itemName = typeof item.name === 'string' ? item.name : '';
        const itemFilename = itemKey.split('/').pop() || itemKey || itemName;
        return (
          itemFilename === imageName ||
          itemKey.endsWith(`/${imageName}`) ||
          itemKey === imageName ||
          itemName === imageName
        );
      });
      if (fileExists) {
        return prevContents;
      }

      return sortEventImagesNewestFirst([...prevContents, newImage]);
    });
  }, []); // Empty dependency array - function never recreates

  useEffect(() => {
    // Wait for authentication to be ready before making API calls
    if (authLoading) return;
    
    // Ensure user is authenticated
    if (!user || !user.id) {
      return;
    }
    
    
    // Fetch event ID for email functionality
    if (hasValidParams) {
      fetchEventId();
    }
  }, [authLoading, user, hasValidParams, fetchEventId]);

  useEffect(() => {
    // Fetch branded content settings when eventId changes
    if (eventId) {
      fetchBrandedContentSettings();
    }
  }, [eventId, fetchBrandedContentSettings]);

  // Connect to socket service on component mount
  useEffect(() => {
    // Wait for authentication to be ready
    if (authLoading) return;

    // Ensure user is authenticated and we have valid customer/event
    if (!user || !user.id || !customer || !event) {
      return;
    }

    // Connect to socket service for S3 events
    try {
      const socketResult = socketService.connect();
      if (socketResult) {
        // Set up S3 event listener
        const socketCleanup = socketService.onS3Event((payload) => {
          handleS3Event(payload);
        });
        
        // Store cleanup function
        socketCleanupRef.current = socketCleanup;
      } else {
        // Socket service disabled or not available
      }
    } catch (error) {
      console.warn('[EventView] Socket service not available:', error.message);
    }
    
    // Cleanup function
    return () => {
      if (socketCleanupRef.current) {
        socketCleanupRef.current();
        socketCleanupRef.current = null;
      }
    };
  }, [authLoading]); // Only depend on authLoading, not user/customer/event

  // Separate effect to connect socket when user/customer/event become available
  useEffect(() => {
    // Only run if auth is ready and we have all required values
    if (authLoading || !user?.id || !customer || !event) {
      return;
    }

    // Check if socket is already connected
    const connectionStatus = socketService.getConnectionStatus();
    if (connectionStatus.connected && socketCleanupRef.current) {
      return;
    }

    // Connect to socket service for S3 events
    try {
      const socketResult = socketService.connect();
      if (socketResult) {
        // Set up S3 event listener
        const socketCleanup = socketService.onS3Event((payload) => {
          handleS3Event(payload);
        });
        
        // Store cleanup function
        socketCleanupRef.current = socketCleanup;
      } else {
        // Socket service disabled or not available
      }
    } catch (error) {
      console.warn('[EventView] Socket service not available:', error.message);
    }
  }, [user?.id, customer, event]); // Only depend on the actual values we need

  // Handle event-specific logic when customer/event params change
  useEffect(() => {
    // Wait for authentication to be ready before making API calls
    if (authLoading) return;

    // Ensure user is authenticated
    if (!user || !user.id) {
      return;
    }

    // Only proceed if we have the parameters from React Router
    if (customer && event) {
      fetchEventContents();
    } else if (customer === undefined && event === undefined) {
      // Only fetch customers if we're definitely on the root path (no parameters at all)
      fetchCustomers();
    }
    // If we have partial parameters (one but not the other), don't do anything yet
  }, [customer, event, fetchEventContents, authLoading, user]);

  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const response = await axios.get(`${API_BASE_URL}/user/customers`);
      if (response.data.success) {
        setCustomers(response.data.customers);
      } else {
        throw new Error('Failed to fetch customers');
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching customers',
        severity: 'error',
      });
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchEvents = async (customerName) => {
    try {
      setLoadingEvents(true);
      const response = await axios.get(`${API_BASE_URL}/user/customers/${encodeURIComponent(customerName)}/events`);
      if (response.data.success) {
        setEvents(response.data.events);
      } else {
        throw new Error('Failed to fetch events');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching events',
        severity: 'error',
      });
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleCustomerChange = (customerName) => {
    setSelectedCustomer(customerName);
    setSelectedEvent('');
    setEvents([]);
    if (customerName) {
      fetchEvents(customerName);
    }
  };

  const handleEventChange = (eventName) => {
    setSelectedEvent(eventName);
  };

  const handleContinue = () => {
    if (selectedCustomer && selectedEvent) {
      navigate(`/${selectedCustomer}/${selectedEvent}`);
    }
  };

  const handleImageSelect = (item) => {
    setSelectedImages(prev => {
      const isAlreadySelected = prev.some(img => img.key === item.key);
      if (isAlreadySelected) {
        return prev.filter(img => img.key !== item.key);
      }
      if (prev.length >= MAX_SELECTED_IMAGES) {
        setSnackbar({
          open: true,
          message: `You can only select up to ${MAX_SELECTED_IMAGES} images`,
          severity: 'warning',
        });
        return prev;
      }
      return [...prev, item];
    });
  };

  const handleEmailSubmit = async () => {

    if (!eventId) {
      setSnackbar({
        open: true,
        message: 'Event not found. Please refresh the page and try again.',
        severity: 'error',
      });
      return;
    }

    if (!selectedImages || selectedImages.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one image to send.',
        severity: 'error',
      });
      return;
    }

    try {
      setIsSendingEmail(true);
        await axios.post(`${API_BASE_URL}/email`, {
          email: emailAddress,
          subject: emailSubject,
          greeting: emailGreeting,
          body: emailBody,
          images: selectedImages.map(img => img.url),
          eventId: eventId
        });
      setEmailDialogOpen(false);
      await resetEmailForm(); // Reset form after successful send
      setSnackbar({
        open: true,
        message: "Your email is on it's way!",
        severity: 'success',
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setSnackbar({
        open: true,
        message: 'Error sending email',
        severity: 'error',
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const loadEmailSettings = async () => {
    if (!eventId) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/events/${eventId}/email-settings`);
      if (response.data.success && response.data.settings) {
        const settings = response.data.settings;
        setEmailSubject(settings.subject || '');
        setEmailGreeting(settings.greeting || '');
        setEmailBody(settings.body || '');
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
      // Don't show error to user, just use defaults
    }
  };

  const resetEmailForm = async () => {
    setEmailAddress('');
    setSelectedImages([]);
    if (eventId) {
      await loadEmailSettings();
    }
  };

  const handleEmailImages = async () => {
    if (selectedImages.length === 0) return;
    await loadEmailSettings();
    setEmailDialogOpen(true);
  };

  // Show customer/event selection if no valid params
  if (!hasValidParams) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        bgcolor: 'background.default' 
      }}>
        <Header />
        <Container maxWidth="sm" sx={{ mt: 12, p: 3 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>
              Select Customer & Event
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Please select a customer and event to view photos
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={selectedCustomer}
                  label="Customer"
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  disabled={loadingCustomers}
                >
                  {loadingCustomers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading customers...
                    </MenuItem>
                  ) : (
                    customers.map((customer) => (
                      <MenuItem key={customer} value={customer}>
                        {customer}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Event</InputLabel>
                <Select
                  value={selectedEvent}
                  label="Event"
                  onChange={(e) => handleEventChange(e.target.value)}
                  disabled={!selectedCustomer || loadingEvents}
                >
                  {loadingEvents ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading events...
                    </MenuItem>
                  ) : (
                    events.map((event) => (
                      <MenuItem key={event} value={event}>
                        {event}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                size="large"
                onClick={handleContinue}
                disabled={!selectedCustomer || !selectedEvent}
                sx={{
                  bgcolor: '#db4481',
                  '&:hover': {
                    bgcolor: '#c13d73',
                  },
                }}
              >
                Continue
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show event view with photos
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      bgcolor: 'background.default' 
    }}>
      <Header />
      <Box sx={{ 
        mt: '90px', // Use margin-top instead of padding-top to push content below header
        p: 3,
        flex: 1
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3 
        }}>
          <Typography variant="h5" sx={{ color: 'text.primary' }}>
            {customer} / {event}
          </Typography>
          <Tooltip title={selectedImages.length === 0 ? "Select images to email" : `Email ${selectedImages.length} selected images`}>
            <span>
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                onClick={handleEmailImages}
                disabled={selectedImages.length === 0 || isSendingEmail}
                sx={{
                  bgcolor: selectedImages.length > 0 && !isSendingEmail ? '#db4481' : 'action.disabled',
                  '&:hover': {
                    bgcolor: selectedImages.length > 0 && !isSendingEmail ? '#c13d73' : 'action.disabled',
                  },
                }}
              >
                {isSendingEmail ? 'Sending Email...' : 'Email Selected Images'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        <ImageGallery
          customer={customer}
          event={event}
          images={eventContents}
          onImageSelect={handleImageSelect}
          selectedImages={selectedImages}
          brandedContentSettings={brandedContentSettings}
          brandedContentImage={brandedContentImage}
        />

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onClose={async () => {
          setEmailDialogOpen(false);
          await resetEmailForm();
        }}>
          <DialogTitle
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="h6" component="span">
                Email Images
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={async () => {
                    setEmailDialogOpen(false);
                    await resetEmailForm();
                  }}
                  disabled={isSendingEmail}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEmailSubmit}
                  variant="contained"
                  disabled={isSendingEmail}
                  startIcon={isSendingEmail ? <CircularProgress size={20} /> : null}
                >
                  {isSendingEmail ? 'Sending...' : 'Send'}
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {isSendingEmail && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="textSecondary">
                    Processing images and sending email... This may take a moment for branded content.
                  </Typography>
                </Box>
              )}
              <TextField
                fullWidth
                label="Email Address"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                margin="normal"
                disabled={isSendingEmail}
              />
              <TextField
                fullWidth
                label="Subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                margin="normal"
                disabled={isSendingEmail}
              />
              <TextField
                fullWidth
                label="Greeting"
                value={emailGreeting}
                onChange={(e) => setEmailGreeting(e.target.value)}
                margin="normal"
                disabled={isSendingEmail}
              />
              <TextField
                fullWidth
                label="Message"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                margin="normal"
                multiline
                rows={4}
                disabled={isSendingEmail}
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default EventView; 